import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { makeAutoObservable } from 'mobx'
import UserProfileService from './../../../../shared/services/UserProfile.service'
import { UIInput, UIInputButton } from './../../../../components/forms/form-fields'
import MDB4Switch from './../../../../components/forms/MDB4Switch'
import UserMetaService from '../../../../shared/services/UserMeta.service'
import { MDBCard, MDBCardHeader, MDBCardBody, MDBBtn, MDBIcon, MDBRow, MDBCol } from 'mdb-react-ui-kit'

import './BeastModeAccountLink.component.scss'

class AcctStore {
  constructor() {
    makeAutoObservable(this)
  }

  isFetching = false
  isSaving = false
  UserMeta = null
  recruiter = ''
  sales = ''
  userId = null
  nextLevelMeta = null
  nextLevel = false

  fetch = async userId => {
    if (!userId || parseInt(this.userId) === parseInt(userId)) {
      this.reset()
      return false
    }

    this.reset()
    this.isFetching = true

    let UserMeta
    try {
      UserMeta = await UserMetaService.search({
        search: { meta_key: '10x---location-id', user_id: userId },
      })
      UserMeta = ((UserMeta && UserMeta?.models) || []).shift()
    } catch (ex) {
      console.log(`Failed to fetch user meta for 10x location ID. ${ex}`)
    }

    let value = {}
    try {
      value = JSON.parse(UserMeta && UserMeta?.meta_value)
    } catch (ex) {}

    this.UserMeta = UserMeta || false
    this.recruiter = value?.recruiter || ''
    this.sales = value?.sales || ''

    let nextLevelMeta
    try {
      nextLevelMeta = await UserMetaService.search({
        search: { meta_key: '10x---next-level', user_id: userId },
      })
      nextLevelMeta = ((nextLevelMeta && nextLevelMeta?.models) || []).shift()
    } catch (ex) {
      console.log(`Failed to fetch user meta for 10x next level. ${ex}`)
    }

    this.nextLevelMeta = nextLevelMeta || null
    this.nextLevel = nextLevelMeta?.meta_value === '1'

    this.isFetching = false

    return UserMeta
  }

  save = async () => {
    const { sales, recruiter } = this,
      meta_value = JSON.stringify({ recruiter, sales })

    this.isSaving = true
    if (this.UserMeta) {
      await UserMetaService.update(this.UserMeta.id, { meta_value })
      this.isSaving = false
      return
    }

    let UserMeta = await this.fetch(this.userId)
    if (UserMeta) {
      await UserMetaService.update(UserMeta.id, { meta_value })
      this.isSaving = false
      return
    }

    UserMeta = {
      user_id: UserProfileService.getUserId(),
      meta_key: '10x---location-id',
      meta_value,
    }
    let result = await UserMetaService.store(UserMeta)

    if (result && result?.id) {
      this.UserMeta = UserMeta
      this.recruiter = JSON.parse(UserMeta.meta_value)?.recruiter || ''
      this.sales = JSON.parse(UserMeta.meta_value)?.sales || ''
    }

    this.isSaving = false
  }

  toggleNextLevel = async () => {
    this.isSaving = true
    const newValue = this.nextLevel ? '0' : '1'

    try {
      if (this.nextLevelMeta && this.nextLevelMeta.id) {
        await UserMetaService.update(this.nextLevelMeta.id, {
          meta_value: newValue,
        })
        this.nextLevelMeta = { ...this.nextLevelMeta, meta_value: newValue }
      } else {
        const meta = {
          user_id: UserProfileService.getUserId(),
          meta_key: '10x---next-level',
          meta_value: newValue,
        }
        const result = await UserMetaService.store(meta)
        if (result && result?.id) {
          this.nextLevelMeta = { ...meta, id: result.id }
        }
      }
      this.nextLevel = newValue === '1'
    } catch (ex) {
      console.error(`Failed to toggle next level. ${ex}`)
    }

    this.isSaving = false
  }

  reset = async () => {
    this.isFetching = false
    this.isSaving = false
    this.UserMeta = null
    this.recruiter = ''
    this.sales = ''
    this.userId = null
    this.nextLevelMeta = null
    this.nextLevel = false
  }
}

const Store = new AcctStore()

const BeastModeAccountLink = ({ userId, onReset }) => {
  const meetsRequirement =
    UserProfileService.isA('system-admin', true) ||
    (UserProfileService.isAssumed() &&
      (UserProfileService.isA('internal-admin', true) ||
        UserProfileService.isA('internal-staff', true) ||
        UserProfileService.isA('system-admin', true) ||
        UserProfileService.isA('agency-owner', true)))

  useEffect(() => {
    if (meetsRequirement && userId) Store.fetch(userId)
  }, [meetsRequirement, userId])

  const renderCard = () => {
    if (!userId || !meetsRequirement) return <></>

    const RecruiterIDInput = (
      <UIInput
        name='recruiter_id'
        label='Recruiter CRM'
        value={Store.recruiter}
        onChange={evt => (Store.recruiter = evt.target.value)}
      />
    )

    const SaveBtn = (
      <MDBBtn type='button' color='default' disabled={Store.isSaving} onClick={() => Store.save()} className='save-btn'>
        {Store.isSaving ? <i className='fa fa-spin fa-spinner'></i> : <MDBIcon icon='save' />}
      </MDBBtn>
    )

    return (
      <MDBCard id='BeastModeAccountLinkComponent'>
        <MDBCardHeader>BeastMode 10x CRM Account</MDBCardHeader>
        <MDBCardBody>
          <MDBRow>
            <MDBCol size='12' className='mt-1 mb-1'>
              <UIInputButton input={RecruiterIDInput} button={SaveBtn} btnPosition={'right'} />
              {Store.recruiter ? (
                <p>
                  <a
                    href={`https://10x.usabg.com/v2/location/${Store.recruiter}`}
                    target='_BLANK'
                    rel='noopener noreferrer'
                  >
                    Open {UserProfileService.get('u_fname')} {UserProfileService.get('u_lname')}&apos;s Recruiter
                    BeastMode 10x CRM
                  </a>
                </p>
              ) : (
                <></>
              )}
            </MDBCol>
            <MDBCol size='12'>
              <hr />
            </MDBCol>
            <MDBCol size='12' className='mt-1 mb-1'>
              <UIInputButton
                input={
                  <UIInput
                    name='sales_id'
                    label='Sales CRM'
                    value={Store.sales}
                    onChange={evt => (Store.sales = evt.target.value)}
                  />
                }
                button={SaveBtn}
                btnPosition={'right'}
              />
              {Store.sales ? (
                <p>
                  <a
                    href={`https://10x.usabg.com/v2/location/${Store.sales}`}
                    target='_BLANK'
                    rel='noopener noreferrer'
                  >
                    Open {UserProfileService.get('u_fname')} {UserProfileService.get('u_lname')}&apos;s BeastMode Sales
                    10x CRM
                  </a>
                </p>
              ) : (
                <></>
              )}
            </MDBCol>
            <MDBCol size='12'>
              <hr />
            </MDBCol>
            <MDBCol size='12' className='mt-1 mb-1'>
              <MDBRow>
                <MDBCol size='7'>
                  <label>
                    Next Level Program&nbsp;
                    {Store.isSaving ? <i className='fa fa-spin fa-spinner'></i> : <></>}
                  </label>
                </MDBCol>
                <MDBCol size='5' style={{ textAlign: 'right' }}>
                  <MDB4Switch
                    labelLeft={'No'}
                    labelRight={'Yes'}
                    disabled={!!Store.isSaving}
                    checked={Store.nextLevel}
                    onChange={() => Store.toggleNextLevel()}
                  />
                </MDBCol>
              </MDBRow>
            </MDBCol>
          </MDBRow>
        </MDBCardBody>
      </MDBCard>
    )
  }

  return renderCard()
}

export default observer(BeastModeAccountLink)
